import PaymentDetailsRequestBuilder from './payment-details';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { getCart } from '../../cart/shop-cart/store/shop-cart.selectors';
import { getPoOrderNumber } from '../../checkout/shop-checkout';
import { hasOnlyFreeTrialProducts } from '../../../common/utils/cart-util';
import { updateToFreeTrialPaymentMethod } from '../../free-trial/utils/free-trial.util';
import {
  getPaymentTypeMagento,
  MagentoOrderRequest,
  PaymentDetails,
  PaymentType
} from '../../checkout/shop-checkout.interface';

export default class MagentoOrderRequestBuilder {
  private paymentDetailsRequestBuilder: PaymentDetailsRequestBuilder;

  constructor(paymentDetailsRequestBuilder: PaymentDetailsRequestBuilder) {
    this.paymentDetailsRequestBuilder = paymentDetailsRequestBuilder;
  }

  /**
   * Builds a Magento order request.
   *
   * If the payment details are missing, it will return null.
   */
  build(): MagentoOrderRequest | null {
    const paymentDetails = this.paymentDetailsRequestBuilder.build();
    if (!paymentDetails) {
      return null;
    }

    const magentoOrderRequest: MagentoOrderRequest = {
      order: {
        paymentMethod: {
          method: getPaymentTypeMagento(paymentDetails.paymentType!),
          ...this.fillPaymentInfoByPaymentMethod(paymentDetails)
        }
      },
      paymentDetails
    }

    if (hasOnlyFreeTrialProducts(getCart(store.getState() as StoreState)?.products)) {
      magentoOrderRequest.order.paymentMethod = updateToFreeTrialPaymentMethod(magentoOrderRequest);
    }

    return magentoOrderRequest;
  }

  /**
   * Fills the payment info by payment method.
   *
   * @param paymentDetails
   * @private
   */
  private fillPaymentInfoByPaymentMethod(paymentDetails: PaymentDetails) {
    if (PaymentType.BRAINTREE_CREDIT_CARD === paymentDetails.paymentType) {
      return {
        additional_data: {
          payment_method_nonce: paymentDetails?.creditCardUniqueIdentifier ? paymentDetails.cvvNonce : paymentDetails.paymentNonce,
          unique_number_identifier: paymentDetails?.creditCardUniqueIdentifier,
          store_in_vault: paymentDetails?.saveInVault,
          device_data: paymentDetails?.deviceData,
          three_d_secure_authentication_id: paymentDetails?.threedsAuthenticationId,
        }
      }
    }

    if (PaymentType.PURCHASE_ORDER === paymentDetails.paymentType) {
      return {
        po_number: getPoOrderNumber(store.getState() as StoreState)
      }
    }

    if ([
      PaymentType.BRAINTREE_APPLE_PAY,
      PaymentType.BRAINTREE_GOOGLE_PAY,
      PaymentType.BRAINTREE_PAYPAL,
      PaymentType.BRAINTREE_VENMO,
      PaymentType.BRAINTREE_IDEAL
    ].includes(paymentDetails.paymentType!)
    ) {
      return {
        additional_data: {
          payment_method_nonce: paymentDetails.paymentNonce,
          device_data: paymentDetails?.deviceData,
        }
      }
    }

    throw new Error(`Payment method ${paymentDetails.paymentType} not supported by magento APIs`);
  }
}
